import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, resolveUrl, CATEGORIES, getSpecRows, isPhysical } from "@/lib/api";
import { generateDatasheet, generateMSDS } from "@/lib/pdf";
import { Search, Download, FileText, X, FlaskConical, ShieldAlert, Droplets, Factory, Layers, Blocks, ArrowLeft, ArrowRight } from "lucide-react";

const CATEGORY_ICONS = {
  "Concrete Admixtures": Droplets,
  "Cement Grinding Aid": Factory,
  "Mortar": Layers,
  "3D Concrete Fence": Blocks,
};

const CATEGORY_IMAGES = {
  "Concrete Admixtures": "/cat/admixtures.jpg",
  "Cement Grinding Aid": "/cat/grinding.jpg",
  "Mortar": "/cat/mortar.jpg",
  "3D Concrete Fence": "/cat/fence.jpg",
};

const SpecRow = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-2.5 border-b border-slate-200 last:border-0">
    <span className="text-sm text-slate-500">{label}</span>
    <span className="text-sm font-medium text-slate-900 text-right font-mono">{value || "-"}</span>
  </div>
);

function ProductModal({ product, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-50/80 backdrop-blur-sm" onClick={onClose} data-testid="product-modal">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white border border-slate-300 rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 z-10 p-2 rounded-full bg-slate-50/70 text-slate-600 hover:text-slate-900" data-testid="modal-close"><X size={20} /></button>
        <div className="h-56 bg-slate-200 overflow-hidden rounded-t-2xl">
          {product.image_url && <img src={resolveUrl(product.image_url)} alt={product.name} className="w-full h-full object-cover" />}
        </div>
        <div className="p-7">
          <div className="flex items-center gap-2 text-xs font-mono text-brand-400"><FileText size={13} /> {product.code} • {product.category}</div>
          <h2 className="mt-2 font-heading text-2xl sm:text-3xl font-bold text-slate-900">{product.name}</h2>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed">{product.description}</p>

          <h3 className="mt-7 text-xs uppercase tracking-widest font-mono text-slate-500">Technical Specifications</h3>
          <div className="mt-3 p-5 rounded-xl bg-slate-50 border border-slate-200">
            {getSpecRows(product).map(([label, value]) => (
              <SpecRow key={label} label={label} value={value} />
            ))}
          </div>

          <div className={`mt-6 grid gap-3 ${isPhysical(product.category) ? "" : "sm:grid-cols-2"}`}>
            <button onClick={() => { generateDatasheet(product); toast.success("Technical Data Sheet downloaded"); }}
              data-testid="modal-download-datasheet"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-brand-500 hover:bg-brand-600 text-white font-semibold transition-colors">
              <Download size={18} /> Data Sheet (TDS)
            </button>
            {!isPhysical(product.category) && (
              <button onClick={() => { generateMSDS(product); toast.success("Safety Data Sheet downloaded"); }}
                data-testid="modal-download-msds"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-gold-500/50 bg-gold-500/10 text-gold-400 hover:bg-gold-500/20 font-semibold transition-colors">
                <ShieldAlert size={18} /> Safety Sheet (MSDS)
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function ProductCard({ p, onView }) {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      className="group flex flex-col rounded-xl overflow-hidden bg-white border border-slate-200 hover:border-brand-500/50 transition-colors" data-testid={`product-card-${p.code}`}>
      <div className="h-44 bg-slate-200 overflow-hidden">
        {p.image_url && <img src={resolveUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
      </div>
      <div className="p-6 flex flex-col flex-1">
        <span className="text-xs font-mono text-brand-400">{p.code}</span>
        <h3 className="mt-2 font-heading text-lg font-semibold text-slate-900">{p.name}</h3>
        <p className="mt-1.5 text-sm text-slate-500 line-clamp-2 flex-1">{p.description}</p>
        <div className="mt-4 space-y-2">
          <button onClick={onView} data-testid={`view-specs-${p.code}`}
            className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full border border-slate-300 text-slate-900 text-sm font-medium hover:border-brand-500/60 transition-colors">
            View Specs
          </button>
          {isPhysical(p.category) ? (
            <button onClick={() => { generateDatasheet(p); toast.success("Data Sheet downloaded"); }} data-testid={`download-${p.code}`}
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full bg-brand-500 hover:bg-brand-600 text-white text-sm font-semibold transition-colors">
              <Download size={15} /> Data Sheet (TDS)
            </button>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => { generateDatasheet(p); toast.success("Data Sheet downloaded"); }} data-testid={`download-${p.code}`}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full bg-brand-500 hover:bg-brand-600 text-white text-sm font-semibold transition-colors">
                <Download size={15} /> TDS
              </button>
              <button onClick={() => { generateMSDS(p); toast.success("Safety Sheet downloaded"); }} data-testid={`download-msds-${p.code}`}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full border border-gold-500/50 bg-gold-500/10 text-gold-400 hover:bg-gold-500/20 text-sm font-semibold transition-colors">
                <ShieldAlert size={15} /> MSDS
              </button>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function Products() {
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState(null);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => toast.error("Failed to load products")).finally(() => setLoading(false));
  }, []);

  const countFor = (c) => products.filter((p) => p.category === c).length;
  const imageFor = (c) => (products.find((p) => p.category === c && p.image_url) || {}).image_url;

  const list = category
    ? products.filter((p) => {
        if (p.category !== category) return false;
        const q = query.toLowerCase();
        return !q || p.name.toLowerCase().includes(q) || p.code.toLowerCase().includes(q) || (p.standard || "").toLowerCase().includes(q);
      })
    : [];

  const openCategory = (c) => { setCategory(c); setQuery(""); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="products-page">
      {/* Header */}
      <section className="relative py-20 border-b border-slate-200 grain overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src="https://images.pexels.com/photos/37733181/pexels-photo-37733181.jpeg?auto=compress&cs=tinysrgb&w=1600" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-slate-50/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <span className="text-xs uppercase tracking-widest font-mono text-brand-400 inline-flex items-center gap-2"><FlaskConical size={13} /> Product Catalog</span>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-extrabold text-slate-900">Technical Data Sheets</h1>
          <p className="mt-4 text-slate-600 max-w-2xl">
            {category ? "Browse the products in this section and download their data sheets." : "Choose a product category to view its data sheets."}
          </p>
        </div>
      </section>

      {loading ? (
        <div className="text-center text-slate-500 py-24">Loading...</div>
      ) : !category ? (
        /* STEP 1 — category selection */
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16" data-testid="category-selection">
          <div className="grid gap-6 sm:grid-cols-2">
            {CATEGORIES.map((c, i) => {
              const Icon = CATEGORY_ICONS[c] || FileText;
              const img = imageFor(c);
              return (
                <motion.button
                  key={c}
                  onClick={() => openCategory(c)}
                  data-testid={`category-card-${c.replace(/\s/g, "-")}`}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.07 }}
                  className="group relative text-left rounded-2xl overflow-hidden border border-slate-200 bg-white hover:border-brand-500/60 transition-colors min-h-[190px]"
                >
                  {img && <img src={resolveUrl(img)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 group-hover:scale-105 transition-all duration-500 [filter:saturate(1.15)_contrast(1.05)]" />}
                  <div className="absolute inset-0 bg-gradient-to-r from-slate-50 via-slate-50/70 to-slate-50/25" />
                  <div className="relative p-8 flex flex-col h-full">
                    <div className="w-16 h-16 rounded-xl bg-white border border-slate-200 flex items-center justify-center group-hover:scale-110 transition-transform overflow-hidden p-2">
                      {CATEGORY_IMAGES[c] ? <img src={CATEGORY_IMAGES[c]} alt={c} className="w-full h-full object-contain" /> : <Icon size={26} className="text-brand-400" />}
                    </div>
                    <h3 className="mt-5 font-heading text-2xl font-bold text-slate-900">{c}</h3>
                    <div className="mt-auto pt-4 flex items-center justify-between">
                      <span className="text-xs font-mono text-slate-500">{countFor(c)} product{countFor(c) === 1 ? "" : "s"}</span>
                      <span className="inline-flex items-center gap-1.5 text-brand-400 font-semibold text-sm">View data sheets <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" /></span>
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </section>
      ) : (
        /* STEP 2 — products in the selected category */
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-12" data-testid="category-products">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <button onClick={() => setCategory(null)} data-testid="back-to-categories"
                className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-brand-400 transition-colors">
                <ArrowLeft size={16} /> All Sections
              </button>
              <h2 className="mt-2 font-heading text-2xl sm:text-3xl font-bold text-slate-900">{category}</h2>
            </div>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search this section..." data-testid="product-search"
                className="w-full bg-white border border-slate-300 rounded-full pl-12 pr-4 py-3 text-slate-900 text-sm focus:border-brand-500 outline-none" />
            </div>
          </div>

          {list.length === 0 ? (
            <div className="text-center text-slate-500 py-20" data-testid="no-products">
              {query ? "No products match your search." : "No data sheets in this section yet."}
            </div>
          ) : (
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((p) => <ProductCard key={p.id} p={p} onView={() => setSelected(p)} />)}
            </div>
          )}
        </section>
      )}

      {selected && <ProductModal product={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
